import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSiteGeofences } from '../hooks/useGeofence';
import { useMyStudents } from '../hooks/useReunification';
import {
  guardianStudentName,
  type GuardianStudentWithLegacyIds,
} from '../services/reunification';
import { useVisitorManagementEnabled } from '../hooks/useVisitorManagement';
import { client } from '../lib/http-client';
import {
  initializeGeofenceNativeBridge,
  registerGeofencesWithNative,
  type GeofenceEnterEventDetail,
} from '../lib/geofence-bridge';
import { useNotificationsDrawer } from '../contexts/NotificationsDrawerContext';
import { toaster } from './ui/toaster';

export function GeofenceLifecycleManager() {
  const { t } = useTranslation();
  const { data: geofences } = useSiteGeofences();
  const { data: myStudents } = useMyStudents();
  const { addLocalNotification } = useNotificationsDrawer();
  const isVmEnabled = useVisitorManagementEnabled();

  // Deduplication state: tracks last trigger timestamp for each geofence
  const lastTriggeredTimes = useRef<Map<string, number>>(new Map());

  // Initialize native callback on mount
  useEffect(() => {
    initializeGeofenceNativeBridge();
  }, []);

  // Register geofences with native shell once they are loaded
  useEffect(() => {
    if (geofences && geofences.length > 0) {
      registerGeofencesWithNative(geofences);
    }
  }, [geofences]);

  // Listen to geofence-enter events from native shell / browser simulation
  useEffect(() => {
    const handleGeofenceEnter = (event: Event) => {
      // Suppress VM posts completely if visitor management feature is disabled
      if (!isVmEnabled) {
        if (import.meta.env.DEV) {
          console.log(
            '[GeofenceLifecycleManager] Visitor management feature flag is OFF. Suppressing geofence PreCheckIn.',
          );
        }
        return;
      }

      const customEvent = event as CustomEvent<GeofenceEnterEventDetail>;
      const { geofenceId, latitude, longitude } = customEvent.detail;

      // Client-side deduplication check: 5 minutes (300,000 ms) cooldown
      const now = Date.now();
      const lastTriggered = lastTriggeredTimes.current.get(geofenceId) || 0;
      if (now - lastTriggered < 300_000) {
        if (import.meta.env.DEV) {
          console.log(
            `[GeofenceLifecycleManager] Geofence ${geofenceId} recently triggered. Suppressing notification.`,
          );
        }
        return;
      }

      // Find the matched geofence to build user-friendly display copy
      const matched = geofences?.find((g) => g.public_id === geofenceId);
      if (!matched) {
        if (import.meta.env.DEV) {
          console.warn(
            `[GeofenceLifecycleManager] Geofence ${geofenceId} not found in registered geofences.`,
          );
        }
        return;
      }

      // Filter students whose school matches the geofence site ID.
      // The real guardian/students payload emits no `id`/`school_id`
      // (see GuardianStudentWithLegacyIds), so this filter cannot match
      // against real payloads and the flow no-ops until a join key ships.
      const linkedStudents: GuardianStudentWithLegacyIds[] =
        myStudents?.results ?? [];
      const matchingStudents = linkedStudents.filter(
        (student) =>
          student.school_id === matched.site && student.id !== undefined,
      );

      if (matchingStudents.length === 0) {
        if (import.meta.env.DEV) {
          console.log(
            `[GeofenceLifecycleManager] No matching students at school ${matched.site} for geofence ${geofenceId}.`,
          );
        }
        return;
      }

      const correlationId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              const v = c === 'x' ? r : (r & 0x3) | 0x8;
              return v.toString(16);
            });

      const siteName = matched.site_name || 'School';
      const fenceName = matched.name || 'Boundary';

      // Call backend API for each student and wait for completion
      const postPromises = matchingStudents.map(async (student) => {
        try {
          await client.ky.post('api/v2/visitor-management/pre-checkins/', {
            json: {
              student: student.id,
              school: student.school_id,
              latitude,
              longitude,
            },
            headers: {
              'X-Correlation-ID': correlationId,
            },
            retry: 0,
          });
          if (import.meta.env.DEV) {
            console.log(
              `[GeofenceLifecycleManager] PreCheckIn successfully created for student ${guardianStudentName(student)} (${student.id}) at school ${student.school_id}`,
            );
          }
          return true;
        } catch (err) {
          if (import.meta.env.DEV) {
            console.error(
              `[GeofenceLifecycleManager] Failed to create PreCheckIn for student ${guardianStudentName(student)}:`,
              err,
            );
          }
          return false;
        }
      });

      Promise.all(postPromises).then((results) => {
        const anySuccess = results.some((success) => success);
        if (anySuccess) {
          // Arm the dedupe only after a successful PreCheckIn, so a transient
          // backend failure doesn't suppress the next legitimate crossing.
          lastTriggeredTimes.current.set(geofenceId, now);

          // 1. Trigger local notification alert in the drawer
          addLocalNotification({
            title: t('geofence.approachingTitle', { site: siteName }),
            body: t('geofence.approachingBody', { site: siteName, fence: fenceName }),
            category: 'alert',
            priority: 'high',
            action_url: null,
          });

          // 2. Trigger active visual Toast alert on the dashboard
          toaster.create({
            title: t('geofence.approachingTitle', { site: siteName }),
            description: t('geofence.enteredBoundary'),
            type: 'success',
          });
        }
      });
    };

    window.addEventListener('geofence-enter', handleGeofenceEnter);
    return () => {
      window.removeEventListener('geofence-enter', handleGeofenceEnter);
    };
  }, [geofences, myStudents, addLocalNotification, isVmEnabled]);

  // This manager component serves as an event controller and does not render visual DOM nodes.
  return null;
}
